import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import useMutate from "../../../../../hooks/useMutate";
import {
  getBoardDocument,
  updateBoardDocument,
} from "../../../../../services/apiBoards";

export function useBoardDocumentState({
  boardId,
  showSummary,
  setShowSummary,
  isPermission,
}) {
  const queryClient = useQueryClient();
  const [summary, setSummary] = useState("");
  const [originalSummary, setOriginalSummary] = useState("");
  const isInitialized = useRef(false);

  const isQueryEnabled = useMemo(
    () => Boolean(showSummary && boardId),
    [showSummary, boardId]
  );

  const { data: boardDocumentData, isLoading: isLoadingDocument } = useQuery({
    queryKey: ["boardDocument", boardId],
    queryFn: () => getBoardDocument(boardId),
    enabled: isQueryEnabled,
    staleTime: 0,
    cacheTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (!showSummary) {
      isInitialized.current = false;
      return;
    }

    if (showSummary && !isInitialized.current) {
      isInitialized.current = true;
    }

    if (boardDocumentData?.data?.board_document) {
      const existingDocument = boardDocumentData.data.board_document;
      setSummary(existingDocument);
      setOriginalSummary(existingDocument);
    } else if (showSummary && !boardDocumentData && !isLoadingDocument) {
      setSummary("");
      setOriginalSummary("");
    }
  }, [showSummary, boardDocumentData, isLoadingDocument]);

  const { mutate, isPending } = useMutate(
    updateBoardDocument,
    ["boardName", "boards", "boardDocument"],
    () => {
      queryClient.invalidateQueries({
        queryKey: ["boardDocument", boardId],
      });
      queryClient.removeQueries({
        queryKey: ["boardDocument", boardId],
      });
    },
    () => {}
  );

  const hasSummaryChanged = useMemo(
    () => summary !== originalSummary,
    [summary, originalSummary]
  );

  const handleClose = useCallback(() => {
    if (hasSummaryChanged && isPermission) {
      mutate({
        board_id: boardId,
        data: { summary },
      });
    }

    setSummary("");
    setOriginalSummary("");
    isInitialized.current = false;
    queryClient.removeQueries({
      queryKey: ["boardDocument", boardId],
    });
    setShowSummary(false);
  }, [
    boardId,
    hasSummaryChanged,
    isPermission,
    mutate,
    queryClient,
    setShowSummary,
    summary,
  ]);

  return {
    summary,
    setSummary,
    isLoadingDocument,
    isPending,
    handleClose,
  };
}
